import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PawPrint, Search, MapPin, Users, Star, Quote, BookOpen, ArrowRight, Calendar, Clock, ChevronDown, Check } from "lucide-react";
import LocationAutocomplete from "../components/LocationAutocomplete";
import UseMyLocationButton from "../components/UseMyLocationButton";
import { CATEGORIES, categoryHref } from "../lib/categories";
import { POSTS } from "../lib/blogPosts";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { CITIES, cityUrl } from "../lib/cities";
import { placeUrl } from "../lib/placeUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (ref, w = 800) => {
  if (!ref) return null;
  if (ref.startsWith("http") || ref.startsWith("/")) return ref;
  return `${BACKEND_URL}/api/photo?ref=${encodeURIComponent(ref)}&w=${w}`;
};

const HERO_FALLBACK_IMG =
  "https://images.unsplash.com/photo-1636544224872-631693e65eaf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxkb2dzJTIwcGxheWluZyUyMGF0JTIwcGFya3xlbnwwfHx8fDE3NzY5MDk0MDZ8MA&ixlib=rb-4.1.0&q=85";

const HERO_IMG =
  "https://images.unsplash.com/photo-1636544224872-631693e65eaf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxkb2dzJTIwcGxheWluZyUyMGF0JTIwcGFya3xlbnwwfHx8fDE3NzY5MDk0MDZ8MA&ixlib=rb-4.1.0&q=85";

const SOCIAL_PROOF = [
  { value: "500+", label: "Dog parents" },
  { value: "5,000+", label: "Searches" },
  { value: "Trusted", label: "by local pet businesses" },
];

const TESTIMONIALS = [
  {
    quote: "We are excited about your app! We've been telling all our clients at events!",
    author: "Woofie's of St. Petersburg",
    role: "Local Groomer",
  },
  {
    quote: "I just created a profile for my dog. Excited to try this!",
    author: "Scenthound",
    role: "South Tampa",
  },
  {
    quote: "That is really cool, well done!!",
    author: "Stephanie M.",
    role: "Dog Parent",
  },
  {
    quote: "Wow! You've done a great job!!",
    author: "Stacy R.",
    role: "Dog Parent",
  },
];

const Landing = () => {
  const [q, setQ] = useState("");
  const [featured, setFeatured] = useState(null);
  const navigate = useNavigate();

  // Personalization bar — sits between navbar & hero. Purely client-side:
  // state is React-only + sessionStorage.pg_personalize. Never persisted
  // to URL, never sent to backend as a search param. The Search page reads
  // the same sessionStorage key to re-rank results (see Search.jsx).
  const [personalize, setPersonalize] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      return JSON.parse(window.sessionStorage.getItem("pg_personalize") || "null");
    } catch {
      return null;
    }
  });
  const [personalizeOpen, setPersonalizeOpen] = useState(false);
  const [personalizeSize, setPersonalizeSize] = useState(personalize?.size || null);
  const [personalizeVibe, setPersonalizeVibe] = useState(personalize?.vibe || null);
  // Logged-in dog profile — populated lazily on mount if a JWT is present.
  // When present, we auto-fill `personalizeSize` and hide the size question
  // in the dropdown ("Size pulled from Rocky's profile"). Not sent to the
  // backend from here — only read.
  const [personalizeDog, setPersonalizeDog] = useState(null);

  useEffect(() => {
    api.get("/featured-park").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  // Auto-populate size from the logged-in user's first dog profile so we
  // don't ask a question they've already answered elsewhere. Silent-fail
  // if unauthenticated / offline — the bar just falls back to asking.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const token = window.localStorage?.getItem("pg_token");
    if (!token) return;
    api.get("/dogs/mine")
      .then((r) => {
        const dogs = Array.isArray(r.data) ? r.data : [];
        const dog = dogs.find((d) => d && d.size);
        if (!dog) return;
        const size = String(dog.size).toLowerCase();
        setPersonalizeDog({ name: dog.name, size });
        // Only auto-fill if the user hasn't already answered this session.
        setPersonalizeSize((prev) => prev || size);
      })
      .catch(() => {});
  }, []);

  const openPersonalize = () => {
    if (!personalizeOpen) track("personalize_bar_opened", {});
    setPersonalizeOpen(true);
  };
  const closePersonalize = () => setPersonalizeOpen(false);
  const submitPersonalize = () => {
    if (!personalizeSize) return;
    const payload = { size: personalizeSize, vibe: personalizeVibe, ts: Date.now() };
    try {
      window.sessionStorage.setItem("pg_personalize", JSON.stringify(payload));
    } catch {
      /* private-mode / storage disabled — best-effort only */
    }
    setPersonalize(payload);
    setPersonalizeOpen(false);
    track("personalize_submitted", { size: payload.size, vibe: payload.vibe });
  };

  const goSearch = (query) => {
    const trimmed = (query || "").trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  };

  // Tap "Use my current location" on the landing page → button has already
  // cached the coords in sessionStorage. Navigate to /search with a flag so
  // the Search page auto-invokes its nearby fetch from the cache (no second
  // permission prompt, no extra round-trip through reverse-geocoding).
  const handleLandingLocated = () => {
    navigate("/search?nearMe=1");
  };

  const featuredPark = featured?.park;
  const featuredMode = featured?.mode || "empty"; // 'busy' | 'popular' | 'empty'
  const featuredCount = featured?.active_checkins || 0;
  const featuredRegion = featured?.park?.city
    ? `${featured.park.city}${featured.park.state && featured.park.state !== "PR" ? `, ${featured.park.state}` : ""}`
    : "your area";
  // Hero photo strategy: try w=600 first (crisper on retina, often cached as
  // parks accrue traffic); on 404 fall back to w=240 (guaranteed cached — it
  // is the ParkCard hero size, so any park that has ever been rendered on
  // the site has this width cached); on 240 also 404, fall back to a static
  // Unsplash image. This is a cheap upgrade over the fixed-width request
  // that was previously blanking the hero when the exact requested width
  // wasn't in cached_photo_bytes.
  const heroPhotoRef = featuredPark?.photos?.[0]?.ref;
  const heroImage = heroPhotoRef
    ? photoUrl(heroPhotoRef, 600)
    : HERO_FALLBACK_IMG;
  const handleHeroError = (e) => {
    const img = e.currentTarget;
    const src = img.getAttribute("src") || "";
    if (heroPhotoRef && src.includes("w=600")) {
      // First-tier miss — try the guaranteed-cached 240 width.
      img.src = photoUrl(heroPhotoRef, 240);
    } else {
      // 240 also failed (or no ref at all) — final static fallback.
      img.src = HERO_FALLBACK_IMG;
    }
  };

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)]">
      {/* Personalization bar — client-side only; state in sessionStorage.
          Collapsed height is fixed (h-11) so opening/closing the dropdown
          below never shifts hero content. The dropdown itself is
          absolutely positioned and overlays the hero. */}
      <style>{`
        @keyframes pg-attention-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 130, 60, 0); }
          50%      { box-shadow: 0 0 0 6px rgba(220, 130, 60, 0.22); }
        }
        .pg-personalize-pulse {
          animation: pg-attention-pulse 1.7s ease-in-out 3;
        }
      `}</style>
      <div className="relative bg-brand-cream border-b border-brand-border-warm/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-11 flex items-center">
          <button
            type="button"
            data-testid="personalize-bar-toggle"
            onClick={personalizeOpen ? closePersonalize : openPersonalize}
            aria-expanded={personalizeOpen}
            aria-controls="personalize-dropdown"
            className={`${!personalize ? "pg-personalize-pulse" : ""} group inline-flex items-center gap-2.5 h-8 px-4 rounded-full text-sm font-semibold transition-colors ${
              personalize
                ? "bg-brand-forest text-white hover:bg-brand-forest-dark"
                : "bg-white border border-brand-border-warm text-brand-ink hover:bg-brand-light-green/40"
            }`}
          >
            {personalize ? (
              <>
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
                <span>Personalized for your dog · tap to adjust</span>
              </>
            ) : (
              <>
                <span className="relative flex items-center">
                  <span aria-hidden className="text-base leading-none">🐾</span>
                  <span
                    aria-hidden
                    className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-orange-500 ring-2 ring-brand-cream"
                  />
                </span>
                <span>Personalize for your dog</span>
                <span className="hidden sm:inline text-brand-ink/50 font-normal">
                  · 3 taps · sorts places by fit
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${personalizeOpen ? "rotate-180" : ""}`}
                />
              </>
            )}
          </button>
        </div>

        {personalizeOpen && (
          <>
            {/* Click-outside catcher. Positioned below the bar so the bar
                itself remains clickable to close. */}
            <button
              type="button"
              aria-label="Close personalization"
              onClick={closePersonalize}
              className="fixed inset-0 top-16 z-30 bg-black/5 cursor-default"
              data-testid="personalize-dropdown-backdrop"
            />
            <div
              id="personalize-dropdown"
              data-testid="personalize-dropdown"
              className="absolute left-0 right-0 top-full z-40 px-4 sm:px-6 pointer-events-none"
            >
              <div className="max-w-xl mx-auto pointer-events-auto bg-white rounded-2xl shadow-warm-lg border border-brand-border-warm p-5 sm:p-6 mt-2 animate-fade-in-up">
                <h3 className="font-heading text-lg font-bold text-brand-ink">
                  Tell us about your dog
                </h3>
                <p className="text-sm text-brand-ink/60 mt-1">
                  We'll sort every place by fit. No signup needed.
                </p>

                {/* Size — hidden when we already pulled it from the
                    logged-in user's dog profile, otherwise 3 chips. */}
                {personalizeDog?.size ? (
                  <div className="mt-4 text-xs text-brand-ink/60">
                    Size pulled from{" "}
                    <span className="font-semibold text-brand-ink">
                      {personalizeDog.name}'s
                    </span>{" "}
                    profile.
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-[0.14em] font-semibold text-brand-ink/50 mb-2">
                      Size
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="personalize-size-row">
                      {[
                        { key: "small", label: "Small" },
                        { key: "medium", label: "Medium" },
                        { key: "large", label: "Large" },
                      ].map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          data-testid={`personalize-size-${s.key}`}
                          onClick={() => setPersonalizeSize(s.key)}
                          className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                            personalizeSize === s.key
                              ? "bg-brand-forest text-white border-brand-forest"
                              : "bg-white text-brand-ink border-brand-border-warm hover:bg-brand-light-green/40"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vibe — 4 chips, optional (submit works without it). */}
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-[0.14em] font-semibold text-brand-ink/50 mb-2">
                    Loves
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="personalize-vibe-row">
                    {[
                      { key: "zoomies", label: "💨 Zoomies" },
                      { key: "sniff", label: "🌿 Sniff & stroll" },
                      { key: "social", label: "🐾 Social" },
                      { key: "chill", label: "😌 Chill" },
                    ].map((v) => (
                      <button
                        key={v.key}
                        type="button"
                        data-testid={`personalize-vibe-${v.key}`}
                        onClick={() => setPersonalizeVibe(v.key)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                          personalizeVibe === v.key
                            ? "bg-brand-forest text-white border-brand-forest"
                            : "bg-white text-brand-ink border-brand-border-warm hover:bg-brand-light-green/40"
                        }`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    data-testid="personalize-cancel"
                    onClick={closePersonalize}
                    className="text-sm text-brand-ink/60 hover:text-brand-ink underline underline-offset-2"
                  >
                    Maybe later
                  </button>
                  <button
                    type="button"
                    data-testid="personalize-submit"
                    onClick={submitPersonalize}
                    disabled={!personalizeSize}
                    className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                      personalizeSize
                        ? "bg-brand-forest text-white hover:bg-brand-forest-dark active:scale-95"
                        : "bg-brand-border-warm/70 text-brand-ink/40 cursor-not-allowed"
                    }`}
                  >
                    See my matches
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden hero-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-16 md:pt-20 md:pb-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <h1 className="font-heading text-[2.8rem] sm:text-5xl md:text-6xl font-black text-brand-ink mt-5 leading-[0.98] tracking-tight">
              Because your dog deserves <br />
              <span className="text-brand-forest italic">the right place</span>
            </h1>
            <p className="text-lg text-brand-ink/70 mt-5 max-w-lg leading-relaxed">
              Find dog-friendly places near you, in cities across the U.S. — parks, beaches, restaurants, vets and more. Real check-ins, real reviews, real dog owners.
            </p>

            <div className="mt-8 max-w-xl">
              <LocationAutocomplete
                testId="landing-search-input"
                value={q}
                onChange={setQ}
                onPick={(picked) => goSearch(picked)}
                placeholder="Search place name, city, or ZIP (e.g. Central Park or Austin)"
                size="lg"
                showButton
                buttonLabel="Find Dog-Friendly Places"
              />
              <div className="mt-3">
                <UseMyLocationButton onLocated={handleLandingLocated} />
              </div>
            </div>

            {/* Category quick-tap row */}
            <div className="mt-6" data-testid="landing-category-row">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-ink/50 mb-3">
                Or jump straight to
              </div>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  return (
                    <Link
                      key={c.key}
                      to={categoryHref(c)}
                      data-testid={`landing-category-${c.key}`}
                      className="inline-flex items-center gap-1.5 bg-white border border-brand-border-warm rounded-full px-3 py-1.5 text-sm font-semibold text-brand-ink hover:shadow-warm transition-all hover:-translate-y-0.5"
                    >
                      <span
                        className="w-5 h-5 rounded-full text-white flex items-center justify-center"
                        style={{ backgroundColor: c.color }}
                      >
                        <Icon className="w-3 h-3" strokeWidth={2.6} />
                      </span>
                      {c.short}
                    </Link>
                  );
                })}
                {/* Private parks — not a Google Places category, so it's
                    appended manually after the lucide-iconed chips.
                    Same dimensions/colors as the rest; emoji replaces
                    the icon since there's no matching lucide glyph. */}
                <Link
                  to="/private-parks"
                  data-testid="landing-category-private"
                  className="inline-flex items-center gap-1.5 bg-white border border-brand-border-warm rounded-full px-3 py-1.5 text-sm font-semibold text-brand-ink hover:shadow-warm transition-all hover:-translate-y-0.5"
                >
                  <span
                    className="w-5 h-5 rounded-full text-white flex items-center justify-center"
                    style={{ backgroundColor: "#4A7C59" }}
                  >
                    <span className="text-[11px] leading-none" aria-hidden>🏡</span>
                  </span>
                  Private
                </Link>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-6 text-sm text-brand-ink/60">
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-brand-forest" /> Live check-ins</div>
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-brand-forest" /> Real reviews</div>
              <div className="hidden sm:flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-forest" /> Local map</div>
            </div>
          </div>

          <div className="md:col-span-6 relative">
            <div className="relative rounded-[2rem] overflow-hidden border border-brand-border-warm shadow-warm-lg aspect-[4/5] max-w-md mx-auto">
              <img src={heroImage} onError={handleHeroError} alt={featuredPark?.name || "Dogs playing at a park"} className="w-full h-full object-cover" />
              <div
                data-testid="happening-now-card"
                className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-sm rounded-2xl p-4 border border-brand-border-warm"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-forest mb-1">
                  {featuredMode === "busy"
                    ? "Happening now"
                    : featuredMode === "popular"
                    ? `Popular in ${featuredRegion}`
                    : `Featured in ${featuredRegion}`}
                </div>
                {featuredPark ? (
                  <Link to={placeUrl(featuredPark)} className="block group">
                    <div className="font-heading text-lg font-bold text-brand-ink leading-tight group-hover:text-brand-forest transition-colors truncate">
                      {featuredPark.name}
                    </div>
                    {featuredMode === "busy" ? (
                      <div className="text-sm text-brand-ink/70 mt-1 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-brand-forest animate-pulse" />
                        {featuredCount} dog{featuredCount === 1 ? "" : "s"} checked in now
                      </div>
                    ) : (
                      <>
                        <div className="text-sm text-brand-ink/80 mt-1 flex items-center gap-1.5">
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                          <span className="font-semibold">{featuredPark.rating?.toFixed(1)}</span>
                          {featuredPark.user_rating_count ? (
                            <span className="text-brand-ink/60 text-xs">({featuredPark.user_rating_count} reviews)</span>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(featuredPark.amenities || []).slice(0, 3).map((a) => (
                            <span
                              key={a}
                              className="text-[10px] font-bold uppercase tracking-wider text-brand-forest bg-brand-light-green px-2 py-0.5 rounded-full"
                            >
                              {a === "Small dog section" ? "Small dogs" : a}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </Link>
                ) : (
                  <>
                    <div className="font-heading text-lg font-bold text-brand-ink leading-tight">
                      Discover the best dog parks near you
                    </div>
                    <div className="text-sm text-brand-ink/70 mt-1">
                      Explore top-rated dog parks loved by local pet parents.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof banner */}
      <section
        data-testid="social-proof-banner"
        className="border-y border-brand-border-warm bg-white/60"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4 text-center">
          {SOCIAL_PROOF.map((item) => (
            <div key={item.label} className="flex flex-col items-center">
              <div className="font-heading text-3xl sm:text-4xl font-bold text-brand-forest tracking-tight">
                {item.value}
              </div>
              <div className="text-sm text-brand-ink/70 mt-1 uppercase tracking-[0.12em] font-semibold">
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Latest from the Blog — single featured post card linking to /blog */}
      {POSTS.length > 0 && (() => {
        const latest = POSTS[0];
        return (
          <section
            data-testid="latest-blog-section"
            className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-4"
          >
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-brand-forest">
                  <BookOpen className="w-3.5 h-3.5" />
                  Latest from the blog
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink mt-2 tracking-tight">
                  Fresh guides for dog parents
                </h2>
              </div>
              <Link
                to="/guides"
                data-testid="latest-blog-all-link"
                className="text-sm font-semibold text-brand-forest hover:text-brand-forest-dark inline-flex items-center gap-1.5"
              >
                See all guides <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <Link
              to={`/guides/${latest.slug}`}
              data-testid="latest-blog-card"
              className="group block bg-white rounded-3xl border border-brand-border-warm hover:border-brand-forest shadow-warm hover:shadow-warm-lg transition-all overflow-hidden md:grid md:grid-cols-5"
            >
              {latest.coverImage && (
                <div className="md:col-span-2 aspect-[16/10] md:aspect-auto md:h-full overflow-hidden bg-brand-light-green">
                  <img
                    src={latest.coverImage}
                    alt={latest.coverAlt || latest.title}
                    loading="lazy"
                    className="w-full h-full object-cover object-top md:min-h-[260px] group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              )}
              <div className="md:col-span-3 p-6 sm:p-8 flex flex-col justify-center">
                <div className="flex flex-wrap items-center gap-3 text-xs text-brand-ink/60 mb-3">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(latest.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {latest.readMinutes} min read
                  </span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-brand-ink group-hover:text-brand-forest transition-colors leading-tight">
                  {latest.title}
                </h3>
                <p className="text-sm sm:text-base text-brand-ink/70 mt-3 leading-relaxed">
                  {latest.excerpt}
                </p>
                <span className="inline-flex items-center gap-1 mt-5 text-sm font-bold text-brand-forest group-hover:gap-2 transition-all">
                  Read the guide <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </section>
        );
      })()}

      {/* Testimonials */}
      <section
        data-testid="testimonials-section"
        className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-4"
      >
        <div className="text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-brand-forest">
            <PawPrint className="w-3.5 h-3.5" />
            From the community
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink mt-3 tracking-tight">
            What Dog Parents Are Saying
          </h2>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              data-testid={`testimonial-${i}`}
              className="bg-brand-cream rounded-3xl p-6 border border-brand-border-warm shadow-warm flex flex-col"
            >
              <Quote className="w-6 h-6 text-brand-forest/70 shrink-0" />
              <blockquote className="font-body text-[0.95rem] text-brand-ink/90 mt-3 leading-relaxed flex-1">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-brand-border-warm/70">
                <div className="font-semibold text-sm text-brand-ink">{t.author}</div>
                <div className="text-xs text-brand-ink/60 mt-0.5">{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Founder */}
      <section
        data-testid="founder-section"
        className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-4"
      >
        <div className="bg-white rounded-[2rem] border border-brand-border-warm shadow-warm-lg overflow-hidden grid md:grid-cols-5 gap-0">
          {/* Photo */}
          <div className="md:col-span-2 bg-brand-light-green relative min-h-[300px] flex items-center justify-center p-6 md:p-8">
            <img
              src="/founder-cesar-jacob.png"
              alt="Cesar Peña and his pit mix Jacob"
              loading="lazy"
              data-testid="founder-photo"
              className="w-44 h-44 md:w-56 md:h-56 rounded-full object-cover border-4 border-white shadow-warm-lg"
            />
            <div className="absolute bottom-4 left-0 right-0 text-center text-[10px] uppercase tracking-[0.2em] font-semibold text-brand-forest/80">
              Cesar &amp; Jacob
            </div>
          </div>

          {/* Copy */}
          <div className="md:col-span-3 p-8 sm:p-10">
            <span className="inline-flex items-center gap-2 bg-brand-light-green text-brand-forest text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
              <PawPrint className="w-3.5 h-3.5" /> Pack Leader
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink mt-4 tracking-tight leading-tight">
              Meet the Pack Leader
            </h2>
            <p className="text-base sm:text-lg text-brand-ink/80 mt-4 leading-relaxed font-body">
              Hey, I'm Cesar — a dog dad in Riverview, FL. I built Pawground because I was tired of
              showing up to empty dog parks or not knowing which restaurants actually welcome dogs.
              My pit mix Jacob and I test every feature ourselves. Pawground is built by a real dog
              parent, for real dog parents.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-forest">
              <span className="w-2 h-2 rounded-full bg-brand-forest animate-pulse" />
              Active builder · Read updates on the <Link to="/guides" className="underline underline-offset-2 hover:text-brand-forest-dark">Pawground guides</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: "Search smart", body: "Filter by off-leash, fenced, water stations, and small dog sections — find the right fit in seconds.", Icon: Search },
            { title: "See who's here", body: "Live check-ins tell you how busy a park is before you drive. Spot your dog's next BFF.", Icon: Users },
            { title: "One-tap check-in", body: "Arrived at the park? Tap once. Your check-in auto-expires after 2 hours so you don't have to remember.", Icon: PawPrint },
          ].map(({ title, body, Icon }) => (
            <div key={title} className="bg-white rounded-3xl p-6 border border-brand-border-warm shadow-warm">
              <span className="w-12 h-12 rounded-2xl bg-brand-light-green text-brand-forest flex items-center justify-center mb-4">
                <Icon className="w-6 h-6" />
              </span>
              <h3 className="font-heading text-xl font-bold text-brand-ink">{title}</h3>
              <p className="text-sm text-brand-ink/70 mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">Featured cities</div>
          <div className="mt-3 flex flex-wrap gap-2 justify-center max-w-3xl mx-auto" data-testid="landing-city-chips">
            {[
              // Featured FL hubs (curated blurbs)
              "tampa-fl", "st-petersburg-fl",
              // Major FL cities (templated — still get a real hub page)
              "miami-fl", "orlando-fl", "jacksonville-fl",
              // More Tampa Bay (curated)
              "riverview-fl", "brandon-fl", "wesley-chapel-fl",
              "valrico-fl", "lithia-fl", "plant-city-fl", "lutz-fl",
              // Puerto Rico (curated)
              "san-juan-pr", "bayamon-pr", "carolina-pr",
              "ponce-pr", "caguas-pr", "mayaguez-pr", "arecibo-pr",
            ]
              .map((slug) => CITIES.find((c) => c.slug === slug))
              .filter(Boolean)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={cityUrl(c)}
                  data-testid={`city-chip-${c.slug}`}
                  className="bg-white border border-brand-border-warm rounded-full px-4 py-2 text-sm text-brand-ink hover:border-brand-forest hover:bg-brand-light-green transition-colors"
                >
                  {c.name}{c.state !== "PR" ? `, ${c.state}` : ""}
                </Link>
              ))}
          </div>

          <div className="mt-10">
            <Link
              to="/guides"
              data-testid="landing-blog-link"
              className="inline-flex items-center gap-2 bg-white border border-brand-border-warm hover:border-brand-forest hover:bg-brand-light-green rounded-full px-5 py-2.5 text-sm font-semibold text-brand-ink transition-all"
            >
              <BookOpen className="w-4 h-4 text-brand-forest" />
              Read our guides — dog tips &amp; recommendations
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Landing;
